import Button from "@/components/base/Button";
import { doctors } from "@/data/doctors";
import { clinicInfo } from "@/mocks/site";

interface CareDoctorProps {
  eyebrow: string;
  title: string;
  ctaLabel?: string;
}

/**
 * Premium doctor spotlight for the specialist care pages. The photograph and
 * details come entirely from the centralized doctor data — swap the image in
 * src/data/doctors.ts and it updates here automatically.
 */
export default function CareDoctor({ eyebrow, title, ctaLabel = "About Your Specialist" }: CareDoctorProps) {
  const doctor = doctors[0];

  return (
    <section className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="reveal relative lg:col-span-5">
            <div className="absolute -left-5 -top-5 bottom-16 w-2/3 rounded-2xl bg-accent-100/70" aria-hidden="true"></div>
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="h-[440px] w-full md:h-[540px]">
                <img
                  src={doctor.image}
                  alt={doctor.imageAlt}
                  title={`${doctor.name} — ${doctor.specialty}, Trichy`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="absolute -bottom-5 right-3 flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,40,80,0.5)] backdrop-blur md:right-6">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-award-line text-xl" aria-hidden="true"></i>
              </span>
              <span>
                <span className="block font-heading text-sm font-semibold text-foreground-950">Senior Consultant</span>
                <span className="block font-label text-xs text-foreground-600">Spine Surgeon, SUHAM Hospitals</span>
              </span>
            </div>
          </div>

          <div className="reveal lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold uppercase tracking-wider text-accent-800">
              {eyebrow}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
              {title}
            </h2>
            <p className="mt-2 font-heading text-xl font-semibold text-primary-700">{doctor.name}</p>
            <p className="mt-1 font-label text-sm font-semibold text-foreground-600">{doctor.designation}</p>

            <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600 md:text-base">{doctor.bio}</p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {doctor.qualification ? (
                <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <p className="font-label text-xs uppercase tracking-wide text-foreground-500">Qualifications</p>
                  <p className="mt-1.5 font-body text-sm font-medium text-foreground-900">{doctor.qualification}</p>
                </div>
              ) : null}
              <div
                className={`rounded-xl border border-background-200 bg-background-50 p-5 ${
                  doctor.qualification ? "" : "sm:col-span-2"
                }`}
              >
                <p className="font-label text-xs uppercase tracking-wide text-foreground-500">Current position</p>
                <p className="mt-1.5 font-body text-sm font-medium text-foreground-900">{doctor.affiliation}</p>
              </div>
            </div>

            <div className="mt-7">
              <p className="font-label text-xs uppercase tracking-wide text-foreground-500">Areas of expertise</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {doctor.expertise.slice(0, 5).map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 font-label text-xs font-medium text-primary-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button to="/about#specialist" size="lg" icon="ri-user-line">
                {ctaLabel}
              </Button>
              <Button to="/appointment" variant="outline" size="lg" icon="ri-calendar-check-line">
                Book a Consultation
              </Button>
              <Button href={clinicInfo.phoneHref} variant="ghost" size="lg" icon="ri-phone-fill">
                {clinicInfo.phoneDisplay}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}